import { create } from "zustand";

/**
 * Admin toast store (#562) — the admin-side port of apps/web's toastStore
 * (admin previously had NO toast infra and fell back to blocking native
 * `alert()`). Same contract: ephemeral success/error/info toasts rendered by
 * the app-root {@link Toaster}, raised imperatively via {@link toast} from
 * mutation `onSuccess`/`onError` handlers.
 */
export type ToastKind = "success" | "error" | "info";

/**
 * Stacking cap: at most this many toasts render at once. The OLDEST is
 * dropped — never the incoming one — so a new toast (and its live-region
 * announcement) always renders, even during a burst.
 */
export const MAX_TOASTS = 3;

export interface ToastItem {
  id: string;
  message: string;
  kind: ToastKind;
}

interface ToastState {
  toasts: ToastItem[];
  /** Enqueue a toast. Returns the generated id (useful for manual dismissal). */
  addToast: (message: string, kind?: ToastKind) => string;
  /** Remove a toast by id (auto-dismiss timer or the dismiss button). */
  removeToast: (id: string) => void;
}

// Monotonic id source — deterministic and collision-free without Date.now/random.
let counter = 0;

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  addToast: (message, kind = "success") => {
    // Collapse a burst of identical toasts into the one already showing.
    const duplicate = get().toasts.find((t) => t.message === message && t.kind === kind);
    if (duplicate) return duplicate.id;
    const id = `toast-${String(++counter)}`;
    set((state) => ({
      toasts: [...state.toasts, { id, message, kind }].slice(-MAX_TOASTS),
    }));
    return id;
  },
  removeToast: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
}));

/**
 * Imperative helper for raising toasts outside React render (mutation handlers,
 * event callbacks). Messages are rendered as escaped text by the Toaster.
 */
export const toast = {
  success: (message: string) => useToastStore.getState().addToast(message, "success"),
  error: (message: string) => useToastStore.getState().addToast(message, "error"),
  info: (message: string) => useToastStore.getState().addToast(message, "info"),
};
